import { useEffect, useState } from "react";
import { Link, Navigate, useNavigate } from "react-router-dom";
import {
  LogOut,
  Plus,
  Trash2,
  Edit3,
  Upload,
  Package,
  TrendingUp,
  Eye,
  ShoppingBag,
  X,
  Save,
  Truck,
  Check,
  Image as ImageIcon,
  KeyRound,
} from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import api, { assetUrl } from "@/lib/api";

const LOGO = "/logo_fluo.png";

const STD_SIZES = ["XS", "S", "M", "L", "XL", "XXL", "Unique"];
const SIZE_OPTIONS = [...STD_SIZES, "__custom__"];
const CATEGORIES = ["T-Shirt", "Pull", "Sweat", "Hoodie", "Casquette", "Bob", "Tote bag", "Accessoire", "Autre"];

function StatCard({ label, value, sub, icon: Icon }) {
  return (
    <div className="fluo-card p-5">
      <div className="flex justify-between items-start">
        <span className="uppercase text-xs tracking-widest text-white/40">{label}</span>
        <Icon className="text-magenta" size={18} />
      </div>
      <div className="font-display text-3xl mt-2">{value}</div>
      {sub && <div className="text-xs text-white/40 mt-1">{sub}</div>}
    </div>
  );
}

function ProductForm({ initial, onSave, onCancel }) {
  const [form, setForm] = useState(
    initial || {
      name: "",
      description: "",
      category: "T-Shirt",
      price_chf: 35,
      weight_grams: 250,
      photos: [],
      sizes: ["XS", "S", "M", "L", "XL", "XXL"].map((n) => ({ name: n, stock: 0 })),
      featured: true,
    }
  );
  const [uploading, setUploading] = useState(false);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const updateSize = (idx, k, v) => {
    const next = [...form.sizes];
    next[idx] = { ...next[idx], [k]: k === "stock" ? parseInt(v) || 0 : v };
    update("sizes", next);
  };
  const addSize = () => {
    const used = new Set(form.sizes.map((s) => s.name));
    const nextStd = STD_SIZES.find((s) => !used.has(s));
    update("sizes", [...form.sizes, { name: nextStd || "Unique", stock: 0 }]);
  };
  const removeSize = (idx) =>
    update("sizes", form.sizes.filter((_, i) => i !== idx));

  const upload = async (files) => {
    setUploading(true);
    try {
      const urls = [];
      for (const file of files) {
        const fd = new FormData();
        fd.append("file", file);
        const res = await api.post("/admin/upload", fd, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        urls.push(res.data.url);
      }
      update("photos", [...(form.photos || []), ...urls]);
    } catch (e) {
      alert("Erreur upload: " + (e.response?.data?.detail || e.message));
    } finally {
      setUploading(false);
    }
  };

  const removePhoto = (i) =>
    update("photos", form.photos.filter((_, idx) => idx !== i));

  const save = async (e) => {
    e.preventDefault();
    const payload = {
      ...form,
      price_chf: parseFloat(form.price_chf),
      weight_grams: parseInt(form.weight_grams),
    };
    if (initial?.id) {
      await api.put(`/admin/products/${initial.id}`, payload);
    } else {
      await api.post("/admin/products", payload);
    }
    onSave();
  };

  return (
    <form
      onSubmit={save}
      className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm overflow-y-auto p-4"
      data-testid="product-form-modal"
    >
      <div className="max-w-3xl mx-auto fluo-card p-6 my-8 bg-black">
        <div className="flex justify-between items-center mb-6">
          <h2 className="font-display text-2xl">
            {initial ? "Modifier produit" : "Nouveau produit"}
          </h2>
          <button
            type="button"
            onClick={onCancel}
            className="text-white/50 hover:text-magenta"
          >
            <X size={24} />
          </button>
        </div>

        <div className="grid md:grid-cols-2 gap-4 mb-4">
          <div>
            <label className="block uppercase text-xs tracking-widest text-white/40 mb-1">
              Nom
            </label>
            <input
              required
              className="fluo-input"
              value={form.name}
              onChange={(e) => update("name", e.target.value)}
              data-testid="form-name"
            />
          </div>
          <div>
            <label className="block uppercase text-xs tracking-widest text-white/40 mb-1">
              Catégorie
            </label>
            <select
              className="fluo-input"
              value={form.category}
              onChange={(e) => update("category", e.target.value)}
              data-testid="form-category"
            >
              {CATEGORIES.map((c) => (
                <option key={c} value={c}>
                  {c}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="block uppercase text-xs tracking-widest text-white/40 mb-1">
              Prix (CHF)
            </label>
            <input
              required
              type="number"
              step="0.01"
              min="0"
              className="fluo-input"
              value={form.price_chf}
              onChange={(e) => update("price_chf", e.target.value)}
              data-testid="form-price"
            />
          </div>
          <div>
            <label className="block uppercase text-xs tracking-widest text-white/40 mb-1">
              Poids (g) — visible admin uniquement
            </label>
            <input
              required
              type="number"
              min="0"
              className="fluo-input"
              value={form.weight_grams}
              onChange={(e) => update("weight_grams", e.target.value)}
              data-testid="form-weight"
            />
          </div>
          <div className="md:col-span-2">
            <label className="block uppercase text-xs tracking-widest text-white/40 mb-1">
              Description
            </label>
            <textarea
              className="fluo-input"
              rows={3}
              value={form.description}
              onChange={(e) => update("description", e.target.value)}
              data-testid="form-description"
            />
          </div>
        </div>

        {/* Photos */}
        <div className="mb-6">
          <label className="block uppercase text-xs tracking-widest text-white/40 mb-2">
            Photos
          </label>
          <div className="grid grid-cols-3 md:grid-cols-5 gap-2 mb-3">
            {form.photos.map((src, i) => (
              <div
                key={i}
                className="relative aspect-square border border-white/10"
              >
                <img
                  src={assetUrl(src)}
                  alt=""
                  className="w-full h-full object-cover"
                />
                <button
                  type="button"
                  onClick={() => removePhoto(i)}
                  className="absolute top-1 right-1 bg-black/80 p-1 hover:text-magenta"
                  data-testid={`remove-photo-${i}`}
                >
                  <Trash2 size={12} />
                </button>
              </div>
            ))}
            <label
              className="aspect-square border border-dashed border-white/20 flex flex-col items-center justify-center cursor-pointer hover:border-magenta hover:text-magenta text-white/40 text-xs"
              data-testid="upload-zone"
            >
              <input
                type="file"
                accept="image/*"
                multiple
                className="hidden"
                onChange={(e) => upload(Array.from(e.target.files))}
              />
              <Upload size={20} />
              <span className="mt-1">{uploading ? "Upload…" : "Ajouter"}</span>
            </label>
          </div>
        </div>

        {/* Sizes */}
        <div className="mb-6">
          <div className="flex justify-between items-center mb-3">
            <label className="uppercase text-xs tracking-widest text-white/40">
              Tailles & stock
            </label>
            <button
              type="button"
              onClick={addSize}
              className="text-xs text-magenta hover:underline"
              data-testid="add-size-btn"
            >
              + ajouter une taille
            </button>
          </div>

          {form.sizes.length > 0 && (
            <div className="grid grid-cols-12 gap-2 px-1 mb-2 text-[10px] uppercase tracking-widest text-white/40">
              <div className="col-span-6 md:col-span-7">Taille</div>
              <div className="col-span-5 md:col-span-4">Stock disponible</div>
              <div className="col-span-1"></div>
            </div>
          )}

          <div className="space-y-2">
            {form.sizes.map((s, i) => {
              const isStandard = STD_SIZES.includes(s.name);
              return (
                <div key={i} className="grid grid-cols-12 gap-2 items-center">
                  <select
                    className="fluo-input col-span-6 md:col-span-7"
                    value={isStandard ? s.name : "__custom__"}
                    onChange={(e) => {
                      if (e.target.value === "__custom__") {
                        updateSize(i, "name", "");
                      } else {
                        updateSize(i, "name", e.target.value);
                      }
                    }}
                    data-testid={`size-select-${i}`}
                  >
                    {STD_SIZES.map((opt) => (
                      <option key={opt} value={opt}>
                        {opt}
                      </option>
                    ))}
                    <option value="__custom__">Personnalisé…</option>
                  </select>
                  <input
                    type="number"
                    min="0"
                    className="fluo-input col-span-5 md:col-span-4"
                    placeholder="0"
                    value={s.stock}
                    onChange={(e) => updateSize(i, "stock", e.target.value)}
                    data-testid={`size-stock-${i}`}
                  />
                  <button
                    type="button"
                    onClick={() => removeSize(i)}
                    className="col-span-1 p-2 text-white/40 hover:text-magenta justify-self-end"
                    aria-label="Supprimer cette taille"
                  >
                    <Trash2 size={16} />
                  </button>
                  {!isStandard && (
                    <input
                      className="fluo-input col-span-12 mt-1"
                      placeholder="Nom personnalisé (ex: 38, Taille L/XL, Enfant…)"
                      value={s.name}
                      onChange={(e) => updateSize(i, "name", e.target.value)}
                      data-testid={`size-custom-name-${i}`}
                    />
                  )}
                </div>
              );
            })}
            {form.sizes.length === 0 && (
              <p className="text-white/40 text-sm italic">
                Aucune taille définie. Clique sur « + ajouter une taille » pour
                commencer.
              </p>
            )}
          </div>
        </div>

        <label className="flex items-center gap-2 mb-6 text-sm">
          <input
            type="checkbox"
            checked={form.featured}
            onChange={(e) => update("featured", e.target.checked)}
            data-testid="form-featured"
          />
          Mettre en avant dans le carrousel d&apos;accueil
        </label>

        <div className="flex gap-3 justify-end">
          <button type="button" onClick={onCancel} className="btn-ghost">
            Annuler
          </button>
          <button type="submit" className="btn-fluo" data-testid="form-save-btn">
            <Save size={16} /> Enregistrer
          </button>
        </div>
      </div>
    </form>
  );
}

function CredentialsModal({ user, onClose, onUpdated }) {
  const [form, setForm] = useState({
    current_password: "",
    new_email: user?.email || "",
    new_password: "",
    confirm_password: "",
  });
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [saving, setSaving] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError(null);
    setSuccess(null);

    if (form.new_password && form.new_password !== form.confirm_password) {
      setError("Les deux nouveaux mots de passe ne correspondent pas");
      return;
    }
    if (!form.current_password) {
      setError("Renseigne ton mot de passe actuel pour confirmer");
      return;
    }

    const payload = { current_password: form.current_password };
    if (form.new_email && form.new_email !== user.email) payload.new_email = form.new_email;
    if (form.new_password) payload.new_password = form.new_password;

    if (!payload.new_email && !payload.new_password) {
      setError("Modifie au moins l'email ou le mot de passe");
      return;
    }

    setSaving(true);
    try {
      const res = await api.put("/auth/credentials", payload);
      // Update stored token (email change re-issues it)
      if (res.data.access_token) {
        localStorage.setItem("admin_token", res.data.access_token);
      }
      setSuccess("Identifiants mis à jour ! Tu peux fermer cette fenêtre.");
      setForm({ ...form, current_password: "", new_password: "", confirm_password: "" });
      onUpdated(res.data.user);
    } catch (err) {
      setError(err.response?.data?.detail || "Erreur lors de la mise à jour");
    } finally {
      setSaving(false);
    }
  };

  return (
    <form
      onSubmit={submit}
      className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm overflow-y-auto p-4"
      data-testid="credentials-modal"
    >
      <div className="max-w-md mx-auto fluo-card p-6 my-12 bg-black">
        <div className="flex justify-between items-center mb-2">
          <h2 className="font-display text-2xl">Mon compte</h2>
          <button type="button" onClick={onClose} className="text-white/50 hover:text-magenta">
            <X size={22} />
          </button>
        </div>
        <p className="text-white/50 text-sm mb-6">
          Change ton email de connexion et/ou ton mot de passe. Ton mot de passe
          actuel est requis pour confirmer.
        </p>

        <label className="block uppercase text-xs tracking-widest text-white/40 mb-1">
          Email de connexion
        </label>
        <input
          type="email"
          className="fluo-input mb-4"
          value={form.new_email}
          onChange={(e) => setForm({ ...form, new_email: e.target.value })}
          data-testid="new-email-input"
        />

        <label className="block uppercase text-xs tracking-widest text-white/40 mb-1">
          Nouveau mot de passe <span className="text-white/30 normal-case">(laisser vide pour ne pas changer)</span>
        </label>
        <input
          type="password"
          autoComplete="new-password"
          minLength={8}
          className="fluo-input mb-4"
          value={form.new_password}
          onChange={(e) => setForm({ ...form, new_password: e.target.value })}
          data-testid="new-password-input"
        />

        {form.new_password && (
          <>
            <label className="block uppercase text-xs tracking-widest text-white/40 mb-1">
              Confirmer le nouveau mot de passe
            </label>
            <input
              type="password"
              autoComplete="new-password"
              className="fluo-input mb-4"
              value={form.confirm_password}
              onChange={(e) => setForm({ ...form, confirm_password: e.target.value })}
              data-testid="confirm-password-input"
            />
          </>
        )}

        <div className="border-t border-white/10 my-4" />

        <label className="block uppercase text-xs tracking-widest text-magenta mb-1">
          Mot de passe actuel (obligatoire)
        </label>
        <input
          type="password"
          autoComplete="current-password"
          required
          className="fluo-input mb-4"
          value={form.current_password}
          onChange={(e) => setForm({ ...form, current_password: e.target.value })}
          data-testid="current-password-input"
        />

        {error && (
          <div className="text-magenta text-sm mb-3" data-testid="credentials-error">
            {error}
          </div>
        )}
        {success && (
          <div className="text-green-400 text-sm mb-3" data-testid="credentials-success">
            ✓ {success}
          </div>
        )}

        <div className="flex justify-end gap-3 mt-2">
          <button type="button" onClick={onClose} className="btn-ghost">
            {success ? "Fermer" : "Annuler"}
          </button>
          <button type="submit" disabled={saving} className="btn-fluo" data-testid="credentials-save">
            <Save size={16} /> {saving ? "Enregistrement…" : "Enregistrer"}
          </button>
        </div>
      </div>
    </form>
  );
}

function ManualOrderForm({ products, onClose, onSave }) {
  const [form, setForm] = useState({
    invoice_number: "",
    items: [
      { product_id: "", product_name: "", size: "M", quantity: 1, unit_price: 0, decrement_stock: true },
    ],
    customer_name: "",
    customer_email: "",
    customer_phone: "",
    customer_address: "",
    payment_method: "cash",
    shipping_cost: 0,
    notes: "",
  });
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);

  const updateItem = (i, k, v) => {
    const items = [...form.items];
    items[i] = {
      ...items[i],
      [k]:
        k === "quantity"
          ? parseInt(v) || 0
          : k === "unit_price"
          ? parseFloat(v) || 0
          : v,
    };
    setForm({ ...form, items });
  };

  const pickProduct = (i, productId) => {
    const items = [...form.items];
    if (!productId) {
      items[i] = { ...items[i], product_id: "" };
    } else {
      const p = products.find((x) => x.id === productId);
      if (p) {
        items[i] = {
          ...items[i],
          product_id: p.id,
          product_name: p.name,
          unit_price: p.price_chf,
          size: p.sizes?.[0]?.name || "Unique",
        };
      }
    }
    setForm({ ...form, items });
  };

  const addItem = () =>
    setForm({
      ...form,
      items: [
        ...form.items,
        { product_id: "", product_name: "", size: "M", quantity: 1, unit_price: 0, decrement_stock: true },
      ],
    });
  const removeItem = (i) =>
    setForm({ ...form, items: form.items.filter((_, idx) => idx !== i) });

  const subtotal = form.items.reduce((s, i) => s + i.unit_price * i.quantity, 0);
  const total = subtotal + (parseFloat(form.shipping_cost) || 0);

  const save = async (e) => {
    e.preventDefault();
    setError(null);
    setSaving(true);
    try {
      const payload = {
        ...form,
        shipping_cost: parseFloat(form.shipping_cost) || 0,
        items: form.items.filter((i) => i.product_name && i.quantity > 0),
      };
      await api.post("/admin/orders/manual", payload);
      onSave();
    } catch (err) {
      setError(err.response?.data?.detail || "Erreur");
    } finally {
      setSaving(false);
    }
  };

  return (
    <form
      onSubmit={save}
      className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm overflow-y-auto p-4"
      data-testid="manual-order-form"
    >
      <div className="max-w-3xl mx-auto fluo-card p-6 my-8 bg-black">
        <div className="flex justify-between items-center mb-6">
          <h2 className="font-display text-2xl">Nouvelle facture manuelle</h2>
          <button type="button" onClick={onClose} className="text-white/50 hover:text-magenta">
            <X size={24} />
          </button>
        </div>

        <p className="text-white/50 text-sm mb-5">
          Pour les ventes sur place (cash, TWINT) ou les facturations B2B
          (sponsors, partenaires). La commande est créée directement avec le
          statut <span className="text-magenta">payé</span>.
        </p>

        <div className="grid md:grid-cols-2 gap-4 mb-4">
          <div>
            <label className="block uppercase text-xs tracking-widest text-white/40 mb-1">
              N° de facture <span className="text-magenta">*</span>
            </label>
            <input
              required
              placeholder="ex: FAC-2027-042 ou STAND-001"
              className="fluo-input"
              value={form.invoice_number}
              onChange={(e) => setForm({ ...form, invoice_number: e.target.value })}
              data-testid="manual-invoice-number"
            />
          </div>
          <div>
            <label className="block uppercase text-xs tracking-widest text-white/40 mb-1">
              Méthode de paiement
            </label>
            <select
              className="fluo-input"
              value={form.payment_method}
              onChange={(e) => setForm({ ...form, payment_method: e.target.value })}
              data-testid="manual-payment-method"
            >
              <option value="cash">Espèces</option>
              <option value="twint">TWINT</option>
              <option value="bank_transfer">Virement bancaire</option>
              <option value="card">Carte (terminal)</option>
              <option value="other">Autre</option>
            </select>
          </div>
        </div>

        <div className="mb-4">
          <div className="flex justify-between items-center mb-2">
            <label className="uppercase text-xs tracking-widest text-white/40">
              Articles
            </label>
            <button type="button" onClick={addItem} className="text-xs text-magenta hover:underline">
              + ajouter une ligne
            </button>
          </div>
          <div className="space-y-2">
            {form.items.map((it, i) => (
              <div key={i} className="border border-white/10 p-3 space-y-2">
                <div className="grid grid-cols-12 gap-2">
                  <select
                    className="fluo-input col-span-12"
                    value={it.product_id}
                    onChange={(e) => pickProduct(i, e.target.value)}
                  >
                    <option value="">— Produit libre (sans catalogue) —</option>
                    {products.map((p) => (
                      <option key={p.id} value={p.id}>
                        {p.name} ({p.price_chf.toFixed(2)} CHF)
                      </option>
                    ))}
                  </select>
                </div>
                <div className="grid grid-cols-12 gap-2">
                  <input
                    placeholder="Nom de l'article"
                    className="fluo-input col-span-6"
                    value={it.product_name}
                    onChange={(e) => updateItem(i, "product_name", e.target.value)}
                    required
                  />
                  <input
                    placeholder="Taille"
                    className="fluo-input col-span-2"
                    value={it.size}
                    onChange={(e) => updateItem(i, "size", e.target.value)}
                  />
                  <input
                    type="number"
                    min="1"
                    placeholder="Qté"
                    className="fluo-input col-span-1"
                    value={it.quantity}
                    onChange={(e) => updateItem(i, "quantity", e.target.value)}
                  />
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    placeholder="Prix unit."
                    className="fluo-input col-span-2"
                    value={it.unit_price}
                    onChange={(e) => updateItem(i, "unit_price", e.target.value)}
                  />
                  <button
                    type="button"
                    onClick={() => removeItem(i)}
                    disabled={form.items.length === 1}
                    className="col-span-1 p-2 text-white/40 hover:text-magenta disabled:opacity-20"
                  >
                    <Trash2 size={14} />
                  </button>
                </div>
                {it.product_id && (
                  <label className="flex items-center gap-2 text-xs text-white/60">
                    <input
                      type="checkbox"
                      checked={it.decrement_stock}
                      onChange={(e) => updateItem(i, "decrement_stock", e.target.checked)}
                    />
                    Décrémenter le stock du produit catalogue
                  </label>
                )}
              </div>
            ))}
          </div>
        </div>

        <div className="grid md:grid-cols-2 gap-4 mb-4">
          <input
            className="fluo-input"
            placeholder="Nom du client (optionnel)"
            value={form.customer_name}
            onChange={(e) => setForm({ ...form, customer_name: e.target.value })}
          />
          <input
            type="email"
            className="fluo-input"
            placeholder="Email client (optionnel)"
            value={form.customer_email}
            onChange={(e) => setForm({ ...form, customer_email: e.target.value })}
          />
          <input
            className="fluo-input"
            placeholder="Téléphone (optionnel)"
            value={form.customer_phone}
            onChange={(e) => setForm({ ...form, customer_phone: e.target.value })}
          />
          <input
            type="number"
            step="0.05"
            min="0"
            className="fluo-input"
            placeholder="Frais d'envoi (0 si vente sur place)"
            value={form.shipping_cost}
            onChange={(e) => setForm({ ...form, shipping_cost: e.target.value })}
          />
          <textarea
            className="fluo-input md:col-span-2"
            rows={2}
            placeholder="Adresse de livraison (optionnel pour vente sur place)"
            value={form.customer_address}
            onChange={(e) => setForm({ ...form, customer_address: e.target.value })}
          />
          <textarea
            className="fluo-input md:col-span-2"
            rows={2}
            placeholder="Notes internes (optionnel)"
            value={form.notes}
            onChange={(e) => setForm({ ...form, notes: e.target.value })}
          />
        </div>

        <div className="border-t border-white/10 pt-4 flex justify-between items-center text-lg font-display">
          <span>Total</span>
          <span className="text-magenta">{total.toFixed(2)} CHF</span>
        </div>

        {error && (
          <div className="mt-3 text-magenta text-sm" data-testid="manual-order-error">
            {error}
          </div>
        )}

        <div className="flex justify-end gap-3 mt-6">
          <button type="button" onClick={onClose} className="btn-ghost">Annuler</button>
          <button type="submit" disabled={saving} className="btn-fluo" data-testid="manual-order-save">
            <Save size={16} /> {saving ? "Enregistrement…" : "Enregistrer la facture"}
          </button>
        </div>
      </div>
    </form>
  );
}

function CarouselEditor({ onClose }) {
  const [images, setImages] = useState([]);
  const [interval, setIntervalSec] = useState(5);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    let cancelled = false;
    api.get("/carousel").then((r) => {
      if (cancelled) return;
      setImages(r.data.images || []);
      setIntervalSec(r.data.interval_seconds || 5);
      setLoading(false);
    });
    return () => {
      cancelled = true;
    };
  }, []);

  const upload = async (files) => {
    setUploading(true);
    try {
      const urls = [];
      for (const file of files) {
        const fd = new FormData();
        fd.append("file", file);
        const res = await api.post("/admin/upload", fd, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        urls.push(res.data.url);
      }
      setImages([...images, ...urls]);
    } catch (e) {
      alert("Erreur upload: " + (e.response?.data?.detail || e.message));
    } finally {
      setUploading(false);
    }
  };

  const removeImg = (i) => setImages(images.filter((_, idx) => idx !== i));

  const move = (i, dir) => {
    const j = i + dir;
    if (j < 0 || j >= images.length) return;
    const next = [...images];
    [next[i], next[j]] = [next[j], next[i]];
    setImages(next);
  };

  const save = async () => {
    await api.put("/admin/carousel", {
      images,
      interval_seconds: parseInt(interval) || 5,
    });
    onClose();
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm overflow-y-auto p-4" data-testid="carousel-editor">
      <div className="max-w-2xl mx-auto fluo-card p-6 my-8 bg-black">
        <div className="flex justify-between items-center mb-6">
          <h2 className="font-display text-2xl">Carrousel d&apos;accueil</h2>
          <button onClick={onClose} className="text-white/50 hover:text-magenta">
            <X size={24} />
          </button>
        </div>

        <p className="text-white/50 text-sm mb-5">
          Choisis les photos qui défilent en haut de la page d&apos;accueil et
          l&apos;intervalle de défilement.
        </p>

        <div className="mb-5">
          <label className="block uppercase text-xs tracking-widest text-white/40 mb-2">
            Intervalle entre les photos
          </label>
          <div className="flex gap-2 flex-wrap">
            {[3, 5, 10].map((sec) => (
              <button
                key={sec}
                type="button"
                onClick={() => setIntervalSec(sec)}
                className={`px-5 py-2 border text-sm uppercase tracking-wider transition-all ${
                  parseInt(interval) === sec
                    ? "border-magenta bg-magenta text-black"
                    : "border-white/20 hover:border-magenta"
                }`}
                data-testid={`interval-${sec}`}
              >
                {sec} secondes
              </button>
            ))}
            <input
              type="number"
              min="1"
              max="60"
              value={interval}
              onChange={(e) => setIntervalSec(e.target.value)}
              className="fluo-input w-24"
              data-testid="interval-custom"
            />
          </div>
        </div>

        <label className="block uppercase text-xs tracking-widest text-white/40 mb-2">
          Photos du carrousel ({images.length})
        </label>
        {loading ? (
          <p className="text-white/40 text-sm">Chargement…</p>
        ) : (
          <>
            {images.length === 0 && (
              <p className="text-white/40 text-sm italic mb-3">
                Aucune photo dans le carrousel. Par défaut, les photos des produits sont utilisées.
              </p>
            )}
            <div className="grid grid-cols-3 md:grid-cols-4 gap-2 mb-4">
              {images.map((src, i) => (
                <div key={i} className="relative aspect-square border border-white/10 group bg-black" data-testid={`carousel-img-${i}`}>
                  <img src={assetUrl(src)} alt="" className="w-full h-full object-cover" />
                  <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 flex items-center justify-center gap-1 transition-opacity">
                    <button
                      type="button"
                      onClick={() => move(i, -1)}
                      disabled={i === 0}
                      className="p-1.5 bg-white/10 hover:bg-magenta hover:text-black disabled:opacity-30 text-xs"
                    >
                      ←
                    </button>
                    <button
                      type="button"
                      onClick={() => move(i, 1)}
                      disabled={i === images.length - 1}
                      className="p-1.5 bg-white/10 hover:bg-magenta hover:text-black disabled:opacity-30 text-xs"
                    >
                      →
                    </button>
                    <button
                      type="button"
                      onClick={() => removeImg(i)}
                      className="p-1.5 bg-white/10 hover:bg-magenta hover:text-black"
                    >
                      <Trash2 size={12} />
                    </button>
                  </div>
                  <span className="absolute top-1 left-1 bg-black/70 text-white text-[10px] px-1.5 py-0.5">
                    {i + 1}
                  </span>
                </div>
              ))}
              <label className="aspect-square border border-dashed border-white/20 flex flex-col items-center justify-center cursor-pointer hover:border-magenta hover:text-magenta text-white/40 text-xs" data-testid="carousel-upload">
                <input
                  type="file"
                  accept="image/*"
                  multiple
                  className="hidden"
                  onChange={(e) => upload(Array.from(e.target.files))}
                />
                <Upload size={20} />
                <span className="mt-1">{uploading ? "Upload…" : "Ajouter"}</span>
              </label>
            </div>
          </>
        )}

        <div className="flex justify-end gap-3 mt-6">
          <button onClick={onClose} className="btn-ghost">Annuler</button>
          <button onClick={save} className="btn-fluo" data-testid="carousel-save">
            <Save size={16} /> Enregistrer
          </button>
        </div>
      </div>
    </div>
  );
}

function ShippingEditor({ onClose }) {
  const [rates, setRates] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    api.get("/shipping/rates").then((r) => {
      if (cancelled) return;
      setRates(r.data.rates);
      setLoading(false);
    });
    return () => {
      cancelled = true;
    };
  }, []);

  const update = (i, k, v) => {
    const next = [...rates];
    next[i] = { ...next[i], [k]: k === "label" ? v : parseFloat(v) || 0 };
    setRates(next);
  };
  const add = () =>
    setRates([...rates, { max_weight_g: 0, price_chf: 0, label: "Nouveau palier" }]);
  const remove = (i) => setRates(rates.filter((_, idx) => idx !== i));

  const save = async () => {
    await api.put("/admin/shipping/rates", { rates });
    onClose();
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm overflow-y-auto p-4">
      <div className="max-w-2xl mx-auto fluo-card p-6 my-8 bg-black">
        <div className="flex justify-between items-center mb-6">
          <h2 className="font-display text-2xl">Frais de port (Poste Suisse)</h2>
          <button onClick={onClose} className="text-white/50 hover:text-magenta">
            <X size={24} />
          </button>
        </div>

        {loading ? (
          <div className="text-white/40">Chargement…</div>
        ) : (
          <>
            <div className="space-y-2 mb-4">
              {rates.map((r, i) => (
                <div key={i} className="grid grid-cols-12 gap-2 items-center">
                  <input
                    className="fluo-input col-span-5"
                    value={r.label}
                    onChange={(e) => update(i, "label", e.target.value)}
                    placeholder="Libellé"
                  />
                  <input
                    type="number"
                    min="0"
                    className="fluo-input col-span-3"
                    value={r.max_weight_g}
                    onChange={(e) => update(i, "max_weight_g", e.target.value)}
                    placeholder="≤ g"
                  />
                  <input
                    type="number"
                    step="0.05"
                    min="0"
                    className="fluo-input col-span-3"
                    value={r.price_chf}
                    onChange={(e) => update(i, "price_chf", e.target.value)}
                    placeholder="CHF"
                  />
                  <button
                    onClick={() => remove(i)}
                    className="p-2 text-white/40 hover:text-magenta"
                  >
                    <Trash2 size={16} />
                  </button>
                </div>
              ))}
            </div>
            <button onClick={add} className="text-xs text-magenta hover:underline mb-6">
              + ajouter un palier
            </button>
            <div className="flex justify-end gap-3">
              <button onClick={onClose} className="btn-ghost">Annuler</button>
              <button onClick={save} className="btn-fluo">
                <Save size={16} /> Enregistrer
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

export default function AdminDashboard() {
  const { user, loading: authLoading, logout } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState("overview");
  const [stats, setStats] = useState(null);
  const [products, setProducts] = useState([]);
  const [orders, setOrders] = useState([]);
  const [editing, setEditing] = useState(null); // null | "new" | productObj
  const [showShipping, setShowShipping] = useState(false);
  const [showCarousel, setShowCarousel] = useState(false);
  const [confirmDeleteId, setConfirmDeleteId] = useState(null);
  const [confirmDeleteOrderId, setConfirmDeleteOrderId] = useState(null);
  const [showManualOrder, setShowManualOrder] = useState(false);
  const [showCredentials, setShowCredentials] = useState(false);
  const [currentUser, setCurrentUser] = useState(null);
  const [orderFilter, setOrderFilter] = useState("to_process");

  useEffect(() => {
    setCurrentUser(user);
  }, [user]);

  const refresh = async () => {
    const [s, p, o] = await Promise.all([
      api.get("/admin/dashboard"),
      api.get("/admin/products"),
      api.get("/admin/orders"),
    ]);
    setStats(s.data);
    setProducts(p.data);
    setOrders(o.data);
  };

  useEffect(() => {
    if (!user) return;
    let cancelled = false;
    (async () => {
      const [s, p, o] = await Promise.all([
        api.get("/admin/dashboard"),
        api.get("/admin/products"),
        api.get("/admin/orders"),
      ]);
      if (cancelled) return;
      setStats(s.data);
      setProducts(p.data);
      setOrders(o.data);
    })();
    return () => {
      cancelled = true;
    };
  }, [user]);

  if (authLoading) return <div className="text-white/40 text-center py-20">Chargement…</div>;
  if (!user) return <Navigate to="/admin/login" replace />;

  const handleLogout = () => {
    logout();
    navigate("/");
  };

  const deleteProduct = async (id) => {
    try {
      await api.delete(`/admin/products/${id}`);
      setConfirmDeleteId(null);
      await refresh();
    } catch (e) {
      alert("Erreur lors de la suppression : " + (e.response?.data?.detail || e.message));
    }
  };

  const deleteOrder = async (id) => {
    try {
      await api.delete(`/admin/orders/${id}`);
      setConfirmDeleteOrderId(null);
      await refresh();
    } catch (e) {
      alert("Erreur : " + (e.response?.data?.detail || e.message));
    }
  };

  const requestDeleteOrder = (id) => {
    setConfirmDeleteOrderId(id);
    setTimeout(() => {
      setConfirmDeleteOrderId((curr) => (curr === id ? null : curr));
    }, 3500);
  };

  const requestDelete = (id) => {
    setConfirmDeleteId(id);
    setTimeout(() => {
      setConfirmDeleteId((curr) => (curr === id ? null : curr));
    }, 3500);
  };

  return (
    <div className="min-h-screen">
      {/* Sidebar / Top */}
      <header className="border-b border-white/10 bg-black/60 backdrop-blur sticky top-0 z-30">
        <div className="max-w-7xl mx-auto px-5 lg:px-8 h-16 flex justify-between items-center">
          <Link to="/" className="flex items-center gap-3">
            <img src={LOGO} alt="" className="h-10" />
            <span className="font-display text-magenta">ADMIN</span>
          </Link>
          <div className="flex gap-2 items-center">
            <span className="hidden md:block text-xs text-white/40">{(currentUser || user).email}</span>
            <button
              onClick={() => setShowCredentials(true)}
              className="btn-ghost"
              data-testid="my-account-btn"
              title="Modifier mon email et mot de passe"
            >
              <KeyRound size={14} /> <span className="hidden sm:inline">Mon compte</span>
            </button>
            <button onClick={handleLogout} className="btn-ghost" data-testid="logout-btn">
              <LogOut size={14} /> <span className="hidden sm:inline">Déconnexion</span>
            </button>
          </div>
        </div>
        <nav className="max-w-7xl mx-auto px-5 lg:px-8 flex gap-1 border-t border-white/5">
          {[
            { k: "overview", label: "Tableau de bord" },
            { k: "products", label: "Produits" },
            { k: "orders", label: "Commandes" },
          ].map((t) => (
            <button
              key={t.k}
              onClick={() => setTab(t.k)}
              className={`px-4 py-3 text-sm uppercase tracking-wider transition-colors ${
                tab === t.k
                  ? "text-magenta border-b-2 border-magenta"
                  : "text-white/50 hover:text-white"
              }`}
              data-testid={`tab-${t.k}`}
            >
              {t.label}
            </button>
          ))}
        </nav>
      </header>

      <main className="max-w-7xl mx-auto px-5 lg:px-8 py-8">
        {tab === "overview" && stats && (
          <div data-testid="overview-tab">
            <h1 className="font-display text-3xl mb-6">Tableau de bord</h1>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
              <StatCard
                label="Ventes (CHF)"
                value={`${stats.total_revenue_chf.toFixed(2)}`}
                sub={`${stats.total_orders_paid} commande${stats.total_orders_paid > 1 ? "s" : ""} payée${stats.total_orders_paid > 1 ? "s" : ""}`}
                icon={TrendingUp}
              />
              <StatCard
                label="Visites totales"
                value={stats.total_visits}
                sub={`${stats.today_visits} aujourd'hui`}
                icon={Eye}
              />
              <StatCard
                label="En attente"
                value={stats.pending_orders}
                sub="paiements"
                icon={ShoppingBag}
              />
              <StatCard
                label="Produits"
                value={stats.product_count}
                sub="actifs"
                icon={Package}
              />
            </div>

            <div className="grid md:grid-cols-2 gap-6 mb-8">
              <div className="fluo-card p-5">
                <h3 className="uppercase text-xs tracking-widest text-white/40 mb-3">
                  Ventes par jour (30j)
                </h3>
                {stats.sales_series.length === 0 ? (
                  <p className="text-white/40 text-sm">Aucune vente pour le moment.</p>
                ) : (
                  <div className="space-y-1.5">
                    {stats.sales_series.map((d) => {
                      const max = Math.max(...stats.sales_series.map((x) => x.amount));
                      const w = max ? (d.amount / max) * 100 : 0;
                      return (
                        <div key={d.date} className="flex items-center gap-2 text-xs">
                          <span className="text-white/40 w-20">{d.date}</span>
                          <div className="flex-1 h-3 bg-white/5">
                            <div
                              className="h-full bg-magenta"
                              style={{ width: `${w}%` }}
                            />
                          </div>
                          <span className="w-16 text-right">{d.amount.toFixed(2)}</span>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>

              <div className="fluo-card p-5">
                <h3 className="uppercase text-xs tracking-widest text-white/40 mb-3">
                  Visites par jour
                </h3>
                {stats.visits_series.length === 0 ? (
                  <p className="text-white/40 text-sm">Aucune visite enregistrée.</p>
                ) : (
                  <div className="space-y-1.5">
                    {stats.visits_series.slice(-15).map((d) => {
                      const max = Math.max(...stats.visits_series.map((x) => x.count));
                      const w = max ? (d.count / max) * 100 : 0;
                      return (
                        <div key={d.date} className="flex items-center gap-2 text-xs">
                          <span className="text-white/40 w-20">{d.date}</span>
                          <div className="flex-1 h-3 bg-white/5">
                            <div
                              className="h-full bg-white/70"
                              style={{ width: `${w}%` }}
                            />
                          </div>
                          <span className="w-12 text-right">{d.count}</span>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>

            <div className="grid md:grid-cols-2 gap-6">
              <div className="fluo-card p-5">
                <h3 className="uppercase text-xs tracking-widest text-white/40 mb-3">
                  Meilleures ventes
                </h3>
                {stats.best_sellers.length === 0 ? (
                  <p className="text-white/40 text-sm">Pas encore de ventes.</p>
                ) : (
                  <ul className="space-y-2 text-sm">
                    {stats.best_sellers.map((b) => (
                      <li key={b.product_id} className="flex justify-between">
                        <span>{b.name}</span>
                        <span className="text-magenta">
                          {b.quantity} vendus · {b.revenue.toFixed(2)} CHF
                        </span>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
              <div className="fluo-card p-5">
                <h3 className="uppercase text-xs tracking-widest text-white/40 mb-3">
                  Stock faible (≤ 3)
                </h3>
                {stats.low_stock.length === 0 ? (
                  <p className="text-white/40 text-sm">Tout est bien rempli ✨</p>
                ) : (
                  <ul className="space-y-2 text-sm">
                    {stats.low_stock.map((s, i) => (
                      <li key={i} className="flex justify-between">
                        <span>
                          {s.name} <span className="text-white/40">({s.size})</span>
                        </span>
                        <span className={s.stock === 0 ? "text-magenta" : ""}>
                          {s.stock === 0 ? "Sold out" : `${s.stock} restants`}
                        </span>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </div>
          </div>
        )}

        {tab === "products" && (
          <div data-testid="products-tab">
            <div className="flex justify-between items-center mb-6 flex-wrap gap-3">
              <h1 className="font-display text-3xl">Produits</h1>
              <div className="flex gap-2 flex-wrap">
                <button
                  onClick={() => setShowCarousel(true)}
                  className="btn-ghost"
                  data-testid="carousel-rates-btn"
                >
                  <ImageIcon size={14} /> Carrousel
                </button>
                <button
                  onClick={() => setShowShipping(true)}
                  className="btn-ghost"
                  data-testid="shipping-rates-btn"
                >
                  Frais d&apos;envoi
                </button>
                <button
                  onClick={() => setEditing("new")}
                  className="btn-fluo"
                  data-testid="new-product-btn"
                >
                  <Plus size={16} /> Nouveau produit
                </button>
              </div>
            </div>

            {products.length === 0 ? (
              <div className="text-center py-16 border border-dashed border-white/10">
                <p className="text-white/40 mb-4">Aucun produit pour le moment.</p>
                <button onClick={() => setEditing("new")} className="btn-fluo">
                  <Plus size={16} /> Ajouter ton premier produit
                </button>
              </div>
            ) : (
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                {products.map((p) => (
                  <div
                    key={p.id}
                    className="fluo-card p-4 flex gap-4"
                    data-testid={`admin-product-${p.id}`}
                  >
                    <div className="w-24 h-24 shrink-0 bg-black border border-white/10">
                      {p.photos?.[0] ? (
                        <img
                          src={assetUrl(p.photos[0])}
                          alt=""
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center text-white/20 text-xs">
                          No photo
                        </div>
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex justify-between gap-2 items-start">
                        <div className="min-w-0">
                          <div className="font-display text-base truncate">
                            {p.name}
                          </div>
                          <div className="text-xs uppercase text-white/40">
                            {p.category} · {p.weight_grams}g
                          </div>
                          <div className="text-magenta font-display mt-1">
                            {p.price_chf.toFixed(2)} CHF
                          </div>
                        </div>
                      </div>
                      <div className="flex flex-wrap gap-1 mt-2">
                        {p.sizes?.map((s) => (
                          <span
                            key={s.name}
                            className={`text-[10px] px-1.5 py-0.5 border ${
                              s.stock === 0
                                ? "border-magenta/40 text-magenta/70"
                                : "border-white/10 text-white/60"
                            }`}
                          >
                            {s.name}: {s.stock}
                          </span>
                        ))}
                      </div>
                      <div className="flex gap-2 mt-3">
                        <button
                          onClick={() => setEditing(p)}
                          className="text-xs text-white/60 hover:text-magenta inline-flex items-center gap-1"
                          data-testid={`edit-${p.id}`}
                        >
                          <Edit3 size={12} /> Modifier
                        </button>
                        {confirmDeleteId === p.id ? (
                          <button
                            onClick={() => deleteProduct(p.id)}
                            className="text-xs inline-flex items-center gap-1 px-2 py-1 bg-magenta text-black font-bold uppercase tracking-wider animate-pulse"
                            data-testid={`confirm-delete-${p.id}`}
                          >
                            <Trash2 size={12} /> Confirmer ?
                          </button>
                        ) : (
                          <button
                            onClick={() => requestDelete(p.id)}
                            className="text-xs text-white/60 hover:text-magenta inline-flex items-center gap-1"
                            data-testid={`delete-${p.id}`}
                          >
                            <Trash2 size={12} /> Supprimer
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {tab === "orders" && (
          <div data-testid="orders-tab">
            <div className="flex justify-between items-center mb-6 flex-wrap gap-3">
              <h1 className="font-display text-3xl">Commandes &amp; factures</h1>
              <div className="flex gap-2 flex-wrap">
                <button
                  onClick={async () => {
                    if (!window.confirm("Créer 5 commandes de démonstration ?")) return;
                    try {
                      await api.post("/admin/demo-orders");
                      alert("5 commandes de démo créées");
                      refresh();
                    } catch (e) {
                      alert("Erreur : " + (e.response?.data?.detail || e.message));
                    }
                  }}
                  className="px-3 py-2 text-xs uppercase tracking-wider border border-white/20 text-white/70 hover:text-magenta hover:border-magenta transition-colors"
                  data-testid="seed-demo-btn"
                  title="Ajoute 5 commandes fictives pour tester"
                >
                  + 5 commandes démo
                </button>
                <button
                  onClick={async () => {
                    if (!window.confirm("Supprimer toutes les commandes de démo ?")) return;
                    try {
                      const r = await api.delete("/admin/demo-orders");
                      alert(`${r.data.deleted} commande(s) démo supprimée(s)`);
                      refresh();
                    } catch (e) {
                      alert("Erreur : " + (e.response?.data?.detail || e.message));
                    }
                  }}
                  className="px-3 py-2 text-xs uppercase tracking-wider border border-white/20 text-white/50 hover:text-red-400 hover:border-red-400 transition-colors"
                  data-testid="delete-demo-btn"
                  title="Supprime toutes les commandes de démo"
                >
                  Nettoyer démo
                </button>
                <button
                  onClick={() => setShowManualOrder(true)}
                  className="btn-fluo"
                  data-testid="manual-order-btn"
                >
                  <Plus size={16} /> Nouvelle facture manuelle
                </button>
              </div>
            </div>

            {/* Filter tabs */}
            {(() => {
              const counts = {
                to_process: orders.filter((o) => o.payment_status === "paid" && o.status !== "cancelled" && o.fulfillment_status !== "shipped").length,
                shipped: orders.filter((o) => o.fulfillment_status === "shipped" && o.status !== "cancelled").length,
                ghost: orders.filter((o) => o.payment_status !== "paid" && o.status !== "cancelled").length,
                cancelled: orders.filter((o) => o.status === "cancelled").length,
                all: orders.length,
              };
              const FILTERS = [
                { k: "to_process", label: "À traiter", color: "text-magenta" },
                { k: "shipped", label: "Expédiées", color: "text-green-400" },
                { k: "ghost", label: "En attente", color: "text-yellow-400" },
                { k: "cancelled", label: "Annulées", color: "text-red-400" },
                { k: "all", label: "Toutes", color: "text-white/60" },
              ];
              return (
                <div className="flex gap-1 mb-6 border-b border-white/10 overflow-x-auto">
                  {FILTERS.map((f) => (
                    <button
                      key={f.k}
                      onClick={() => setOrderFilter(f.k)}
                      className={`px-4 py-2.5 text-xs uppercase tracking-wider whitespace-nowrap transition-colors ${
                        orderFilter === f.k
                          ? `${f.color} border-b-2 border-current`
                          : "text-white/40 hover:text-white"
                      }`}
                      data-testid={`filter-${f.k}`}
                    >
                      {f.label}{" "}
                      <span className="opacity-60">({counts[f.k]})</span>
                    </button>
                  ))}
                </div>
              );
            })()}

            {(() => {
              const filtered = orders.filter((o) => {
                if (orderFilter === "to_process")
                  return o.payment_status === "paid" && o.status !== "cancelled" && o.fulfillment_status !== "shipped";
                if (orderFilter === "shipped")
                  return o.fulfillment_status === "shipped" && o.status !== "cancelled";
                if (orderFilter === "ghost")
                  return o.payment_status !== "paid" && o.status !== "cancelled";
                if (orderFilter === "cancelled") return o.status === "cancelled";
                return true;
              });
              if (filtered.length === 0) {
                const msg = {
                  to_process: "✨ Aucune commande à traiter pour le moment. Bien joué !",
                  shipped: "Aucune commande expédiée encore.",
                  ghost: "Aucune session de paiement abandonnée.",
                  cancelled: "Aucune commande annulée.",
                  all: "Pas encore de commande.",
                }[orderFilter];
                const help = orderFilter === "ghost" && (
                  <p className="text-white/40 text-xs mt-2 max-w-md mx-auto">
                    Les commandes &laquo;&nbsp;en attente&nbsp;&raquo; sont des paniers que des clients
                    ont commencé à payer mais n&apos;ont jamais validés (onglet fermé,
                    carte refusée…). Aucun argent n&apos;a été débité. Tu peux les
                    supprimer librement via l&apos;icône 🗑.
                  </p>
                );
                return (
                  <div className="text-center py-16 border border-dashed border-white/10">
                    <p className="text-white/60">{msg}</p>
                    {help}
                  </div>
                );
              }
              return (
                <div className="space-y-3">
                  {filtered.map((o) => (
                  <div
                    key={o.id}
                    className="fluo-card p-4"
                    data-testid={`order-${o.id}`}
                  >
                    <div className="flex justify-between flex-wrap gap-3 items-start">
                      <div>
                        <div className="font-display text-lg flex items-center flex-wrap gap-2">
                          {o.invoice_number && (
                            <span className="text-magenta text-sm">#{o.invoice_number}</span>
                          )}
                          {o.customer_email || o.address?.full_name || "Sans nom"}
                          <span
                            className={`text-[10px] uppercase tracking-widest px-2 py-0.5 border ${
                              o.payment_status === "paid"
                                ? "border-magenta text-magenta"
                                : "border-white/20 text-white/40"
                            }`}
                          >
                            {o.payment_status === "paid" ? "Payé" : o.payment_status}
                          </span>
                          {o.order_type === "manual" && (
                            <span className="text-[10px] uppercase tracking-widest px-2 py-0.5 border border-white/30 text-white/70">
                              {o.payment_method || "manuel"}
                            </span>
                          )}
                          {o.status === "cancelled" && (
                            <span className="text-[10px] uppercase tracking-widest px-2 py-0.5 border border-red-500/60 text-red-400 bg-red-500/10">
                              Annulée
                            </span>
                          )}
                          {o.payment_status === "paid" && o.status !== "cancelled" && (
                            <span
                              className={`text-[10px] uppercase tracking-widest px-2 py-0.5 border inline-flex items-center gap-1 ${
                                o.fulfillment_status === "shipped"
                                  ? "border-green-500/60 text-green-400 bg-green-500/10"
                                  : "border-yellow-500/60 text-yellow-400 bg-yellow-500/10"
                              }`}
                            >
                              {o.fulfillment_status === "shipped" ? (
                                <>
                                  <Check size={11} /> Envoyée
                                </>
                              ) : (
                                <>
                                  <Truck size={11} /> À envoyer
                                </>
                              )}
                            </span>
                          )}
                        </div>
                        <div className="text-xs text-white/40">
                          {o.created_at?.slice(0, 19).replace("T", " ")}
                          {o.fulfilled_at && o.fulfillment_status === "shipped" && (
                            <span className="ml-2 text-green-400/80">
                              · envoyée le {o.fulfilled_at.slice(0, 10)}
                            </span>
                          )}
                        </div>
                        <div className="text-xs mt-1 text-white/60">
                          {o.address?.full_name} {o.address?.street && `· ${o.address.street}, `}
                          {o.address?.postal_code} {o.address?.city}{o.address?.country && ` (${o.address.country})`}
                          {o.address?.phone && ` · ${o.address.phone}`}
                        </div>
                        {o.notes && (
                          <div className="text-xs mt-1 italic text-white/50">
                            📝 {o.notes}
                          </div>
                        )}
                      </div>
                      <div className="text-right flex flex-col items-end gap-2">
                        <div>
                          <div className="font-display text-xl text-magenta">
                            {o.total.toFixed(2)} CHF
                          </div>
                          <div className="text-xs text-white/40">
                            {o.total_weight_g ? `Poids: ${o.total_weight_g}g · ` : ""}
                            Envoi: {o.shipping_cost.toFixed(2)} CHF
                          </div>
                        </div>
                        <div className="flex gap-2 flex-wrap justify-end">
                          {o.payment_status === "paid" && o.status !== "cancelled" && (
                            <button
                              onClick={async () => {
                                await api.put(`/admin/orders/${o.id}/fulfill`);
                                refresh();
                              }}
                              className={
                                o.fulfillment_status === "shipped" ? "btn-ghost" : "btn-fluo"
                              }
                              style={
                                o.fulfillment_status === "shipped"
                                  ? { padding: "0.5rem 1rem", fontSize: "0.75rem" }
                                  : { padding: "0.5rem 1rem", fontSize: "0.75rem" }
                              }
                              data-testid={`fulfill-${o.id}`}
                            >
                              {o.fulfillment_status === "shipped" ? (
                                <>Annuler envoi</>
                              ) : (
                                <>
                                  <Truck size={14} /> Marquer comme envoyée
                                </>
                              )}
                            </button>
                          )}
                          {confirmDeleteOrderId === o.id ? (
                            <button
                              onClick={() => deleteOrder(o.id)}
                              className="text-xs inline-flex items-center gap-1 px-3 py-2 bg-magenta text-black font-bold uppercase tracking-wider animate-pulse"
                              data-testid={`confirm-delete-order-${o.id}`}
                            >
                              <Trash2 size={12} /> Confirmer ?
                            </button>
                          ) : (
                            <button
                              onClick={() => requestDeleteOrder(o.id)}
                              className="text-xs text-white/50 hover:text-magenta inline-flex items-center gap-1 px-2 py-2"
                              data-testid={`delete-order-${o.id}`}
                              title="Supprimer cette commande"
                            >
                              <Trash2 size={14} />
                            </button>
                          )}
                        </div>
                      </div>
                    </div>
                    <ul className="mt-3 text-sm text-white/70 space-y-0.5 border-t border-white/5 pt-2">
                      {o.items.map((i) => (
                        <li key={`${i.product_id}-${i.size}`}>
                          • {i.product_name} ({i.size}) × {i.quantity} —{" "}
                          {i.subtotal.toFixed(2)} CHF
                        </li>
                      ))}
                    </ul>
                  </div>
                ))}
              </div>
              );
            })()}
          </div>
        )}
      </main>

      {editing && (
        <ProductForm
          initial={editing === "new" ? null : editing}
          onSave={() => {
            setEditing(null);
            refresh();
          }}
          onCancel={() => setEditing(null)}
        />
      )}
      {showShipping && <ShippingEditor onClose={() => { setShowShipping(false); refresh(); }} />}
      {showCarousel && <CarouselEditor onClose={() => setShowCarousel(false)} />}
      {showManualOrder && (
        <ManualOrderForm
          products={products}
          onClose={() => setShowManualOrder(false)}
          onSave={() => { setShowManualOrder(false); refresh(); }}
        />
      )}
      {showCredentials && (
        <CredentialsModal
          user={currentUser || user}
          onClose={() => setShowCredentials(false)}
          onUpdated={(u) => setCurrentUser(u)}
        />
      )}
    </div>
  );
}
